import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CarPickerComponent, TextInputComponent } from '@wz-forms';
import { AutoUnsubscribeComponent } from '@wz-utilities/auto-unsubscribe.component';
import { getDateUtc } from '@wz-utilities/date.utilities';
import { markAllControlsAsDirty } from '@wz-utilities/form.utilities';
import { AutomobileRiskModels } from 'models/automobile-risk.models';
import { QuotationModels } from 'models/quotation.models';
import { Observable, throwError as observableThrowError, of } from 'rxjs';
import { AssureInfoFormComponent } from '../assure-info-form/assure-info-form.component';
import { CarQuotationSubscriptionFormComponent } from '../car-quotation-subscription-form/car-quotation-subscription-form.component';
import { InfosFormComponent } from '../infos-form/infos-form.component';

@Component({
  selector: 'wz-car-quotation-form',
  templateUrl: './car-quotation-form.component.html',
  styleUrls: ['./car-quotation-form.component.scss'],
  standalone: false,
})
export class CarQuotationFormComponent
  extends AutoUnsubscribeComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChildren(TextInputComponent)
  inputs = new QueryList<TextInputComponent>();
  @ViewChild(AssureInfoFormComponent, { static: true })
  assureInfoComponent!: AssureInfoFormComponent;
  @ViewChild(CarPickerComponent, { static: true })
  carPickerComponent!: CarPickerComponent;
  @ViewChild(InfosFormComponent, { static: true })
  infosFormComponent!: InfosFormComponent;
  @ViewChild(CarQuotationSubscriptionFormComponent, { static: true })
  carSubscriptionFormComponent!: CarQuotationSubscriptionFormComponent;

  isTunnelApp = false;
  @Input()
  withSubscription = false;
  @Input()
  policyCreationDate!: Date;
  @Input()
  disabled = false;
  @Input()
  expandGroups = true;
  @Input()
  isTypeMineEditable = false;

  public usage!: QuotationModels.UsageEnum;
  public datePermis!: Date;
  public zipCode!: string;
  public effectDate: Date | null = getDateUtc();

  private _risk!: AutomobileRiskModels.Form;
  get risk() {
    return this._risk;
  }
  @Input()
  set risk(risk: AutomobileRiskModels.Form) {
    this._risk = risk;
    if (risk && this.riskForm) {
      this.patchValue(risk);
      if (risk.assureInfos && risk.assureInfos.address) {
        this.zipCode = risk.assureInfos.address.cityZipCode!;
      }
    }
  }

  public riskForm!: FormGroup;

  constructor(private fb: FormBuilder) {
    super();
  }

  ngOnInit() {
    this.riskForm = this.createForm();
    if (this.withSubscription) {
      this.riskForm.addControl(
        'subscriptionInfo',
        this.carSubscriptionFormComponent.formGroup,
      );
    }

    if (this._risk) {
      this.patchValue(this._risk);
    }
  }

  ngAfterViewInit() {}

  public patchValue(risk: AutomobileRiskModels.Form) {
    this.riskForm.patchValue(risk);
    this.infosFormComponent.onPatchValue(risk.infos!);
  }

  private createForm() {
    const grp = this.fb.group({
      assureInfos: this.assureInfoComponent.formGroup,
      car: this.fb.control(null, Validators.required),
      infos: this.infosFormComponent.formGroup,
    });

    return grp;
  }

  onUsageChange(usage: QuotationModels.UsageEnum) {
    this.usage = usage;
  }

  onDatePermisChange(date: Date) {
    this.datePermis = date;
  }

  onEffectDateChange(date: Date) {
    this.effectDate = date;
  }

  onZipCodeChange(zip: string) {
    this.zipCode = zip;
  }

  submit(): Observable<AutomobileRiskModels.Form> {
    if (this.riskForm.valid) {
      return of(this.riskForm.value);
    } else {
      markAllControlsAsDirty(this.riskForm, true);
      const errors = this.inputs.filter((i) => i.errorState);
      if (errors && errors.length > 0) {
        errors[0].focus();
      }

      return observableThrowError('Form invalid');
    }
  }
}
